import { useMemo, useRef, useState, useEffect } from "react";
import { MagnifyingGlass, CaretDown, X, Buildings, User } from "@phosphor-icons/react";

/**
 * Searchable dropdown for large lists (clients, chantiers, techs…).
 * Replaces the plain shadcn Select which cannot filter through 200+ items.
 *
 * Props:
 *   items        — [{ id, name, ...extra }]
 *   value        — currently selected id
 *   onChange     — (id) => void
 *   placeholder  — trigger placeholder
 *   getLabel     — (item) => string  (defaults to item.name)
 *   getSubtitle  — (item) => string  (optional, shown in muted below name)
 *   testid       — data-testid prefix
 *   emptyLabel   — text when no match
 */
export default function SearchablePicker({
  items = [],
  value,
  onChange,
  placeholder = "Choisir…",
  getLabel = (i) => i.name,
  getSubtitle,
  testid = "searchable-picker",
  emptyLabel = "Aucun résultat",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  const selected = items.find((i) => i.id === value) || null;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items.slice(0, 500);
    const tokens = needle.split(/\s+/).filter(Boolean);
    return items
      .filter((i) => {
        const hay = `${getLabel(i) || ""} ${getSubtitle?.(i) || ""} ${i.email || ""} ${i.phone || ""} ${i.city || ""} ${i.postal_code || ""}`.toLowerCase();
        return tokens.every((t) => hay.includes(t));
      })
      .slice(0, 500);
  }, [q, items, getLabel, getSubtitle]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Focus search input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-10 rounded-md bg-white/5 border border-white/10 hover:border-white/25 px-3 flex items-center gap-2 text-left transition-colors"
        data-testid={`${testid}-trigger`}
      >
        <div className="flex-1 min-w-0">
          {selected ? (
            <div className="text-sm truncate">{getLabel(selected)}</div>
          ) : (
            <div className="text-sm text-white/40">{placeholder}</div>
          )}
          {selected && getSubtitle?.(selected) && (
            <div className="text-[10px] text-white/40 truncate">{getSubtitle(selected)}</div>
          )}
        </div>
        {selected && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="text-white/40 hover:text-white p-1 rounded"
          >
            <X size={12} />
          </span>
        )}
        <CaretDown size={14} className={`text-white/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 rounded-xl bg-[#0A0A0A] border border-white/15 shadow-2xl overflow-hidden flex flex-col max-h-80"
          data-testid={`${testid}-panel`}
        >
          <div className="p-2 border-b border-white/10 relative">
            <MagnifyingGlass size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher…"
              className="w-full h-9 bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 text-sm focus:outline-none focus:border-[#005CFF]/50"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-white/40">{emptyLabel}</div>
            ) : (
              filtered.map((i) => {
                const isSelected = i.id === value;
                return (
                  <button
                    type="button"
                    key={i.id}
                    onClick={() => { onChange(i.id); setOpen(false); setQ(""); }}
                    className={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-white/5 border-b border-white/5 last:border-b-0 ${
                      isSelected ? "bg-[#005CFF]/10" : ""
                    }`}
                    data-testid={`${testid}-item-${i.id}`}
                  >
                    <span className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center shrink-0 mt-0.5">
                      {i.type === "particulier" ? (
                        <User size={12} className="text-white/50" />
                      ) : (
                        <Buildings size={12} className="text-white/50" />
                      )}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">{getLabel(i)}</span>
                      {getSubtitle?.(i) && (
                        <span className="block text-[10px] text-white/40 truncate">{getSubtitle(i)}</span>
                      )}
                    </span>
                    {isSelected && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#7CB2FF] shrink-0 mt-1">Choisi</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
          {items.length > filtered.length && q && (
            <div className="px-3 py-1.5 text-[9px] text-white/30 border-t border-white/5 uppercase tracking-widest">
              {filtered.length} / {items.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
